import React, { forwardRef } from 'react';
import { BaseComponentProps, DisableableProps } from '../types/componentThemes';

export interface InputProps
  extends BaseComponentProps,
    DisableableProps,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'className' | 'style'> {
  /** Input type */
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
  /** Label text displayed above input */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Helper text displayed below input */
  helperText?: string;
  /** Error message - when provided, input shows error state */
  error?: string;
  /** Input value */
  value?: string | number;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Whether the field is required */
  required?: boolean;
  /** ID for the input element */
  id?: string;
  /** Name attribute for the input */
  name?: string;
}

/**
 * Input Component
 * 
 * A versatile input component supporting multiple types with consistent styling.
 * Includes label, helper text, error states, and full accessibility support.
 * 
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   helperText="We'll never share your email"
 * />
 * 
 * <Input
 *   label="Password"
 *   type="password"
 *   error="Password is required"
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      label,
      placeholder,
      helperText,
      error,
      disabled = false,
      required = false,
      className = '',
      style,
      testId,
      id,
      name,
      value,
      onChange,
      ...rest
    },
    ref
  ) => {
    // Generate ID if not provided
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    // Base input classes
    const baseClasses = [
      'w-full',
      'px-4',
      'py-2.5',
      'rounded-lg',
      'border',
      'bg-white',
      'text-neutral-900',
      'placeholder:text-neutral-500',
      'transition-colors',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-0',
    ];

    // State-specific classes
    const stateClasses = error
      ? [
          'border-error-DEFAULT',
          'focus:border-error-DEFAULT',
          'focus:ring-error-DEFAULT',
        ]
      : [
          'border-neutral-300',
          'focus:border-primary-500',
          'focus:ring-primary-500',
        ];

    // Disabled classes
    const disabledClasses = disabled
      ? ['bg-neutral-100', 'cursor-not-allowed', 'opacity-60']
      : [];

    // Combine all classes
    const inputClasses = [
      ...baseClasses,
      ...stateClasses,
      ...disabledClasses,
      className,
    ].join(' ');

    // Handle change event
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e.target.value);
      }
    };

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-neutral-700 mb-1.5"
          >
            {label}
            {required && <span className="text-error-DEFAULT ml-1">*</span>}
          </label>
        )}

        {/* Input */}
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          value={value}
          onChange={handleChange}
          style={style}
          data-testid={testId}
          aria-invalid={!!error}
          aria-describedby={
            error
              ? `${inputId}-error`
              : helperText
              ? `${inputId}-helper`
              : undefined
          }
          {...rest}
        />

        {/* Error message */}
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1.5 text-sm text-error-DEFAULT"
            role="alert"
          >
            {error}
          </p>
        )}

        {/* Helper text */}
        {!error && helperText && (
          <p
            id={`${inputId}-helper`}
            className="mt-1.5 text-sm text-neutral-500"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
